import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle2,
  ChevronDown,
  Mail,
  Phone,
  Search,
  Users,
  WalletCards,
  XCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoaderProgress from '../components/ui/loader-progress';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
}));

const StatWidget = ({ title, value, icon: Icon, color, active, onClick }) => (
  <Paper
    onClick={onClick}
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      borderColor: active ? color : 'rgba(145, 158, 171, 0.24)',
    }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const columns = [
  { key: 'ragione_sociale', label: 'Cliente' },
  { key: 'codice', label: 'Codice' },
  { key: 'piva_cf', label: 'P.IVA / CF' },
  { key: 'azienda', label: 'Aziende' },
  { key: 'anno', label: 'Ultimo anno' },
  { key: 'occorrenze', label: 'Occorrenze' },
  { key: 'total_contratti', label: 'Contratti dossier' },
];

export default function PasspEsposizioneContabile() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    soggetti_univoci: 0,
    con_contratti: 0,
    senza_contratti: 0,
  });
  const [orderBy, setOrderBy] = useState('ragione_sociale');
  const [order, setOrder] = useState('asc');
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [azienda, setAzienda] = useState('all');
  const [anno, setAnno] = useState('all');
  const [aziendeOptions, setAziendeOptions] = useState([]);
  const [anniOptions, setAnniOptions] = useState([]);
  const [aziendeDialogOpen, setAziendeDialogOpen] = useState(false);
  const [aziendeDialogLoading, setAziendeDialogLoading] = useState(false);
  const [selectedAziendeDetails, setSelectedAziendeDetails] = useState([]);
  const [selectedClienteLabel, setSelectedClienteLabel] = useState('');
  const [contrattoStatus, setContrattoStatus] = useState('all');
  const [tipoSoggetto, setTipoSoggetto] = useState('all');
  const [contattiStatus, setContattiStatus] = useState('all');
  const [perf, setPerf] = useState({
    listNetMs: 0,
    listApiMs: 0,
    listBreakdown: null,
    statsNetMs: 0,
    statsApiMs: 0,
    statsBreakdown: null,
  });
  const perfDebugEnabled =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf') === '1';

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(searchInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchFilters = async () => {
    try {
      const res = await api.get('/passp-esposizione-contabile/filters/options');
      setAziendeOptions(res.data?.aziende || []);
      setAnniOptions(res.data?.anni || []);
    } catch (_error) {
      toast.error('Errore nel caricamento filtri Esposizione Contabile');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const startedAt = performance.now();
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection: order,
      };
      if (searchValue) params.search = searchValue;
      if (azienda !== 'all') params.azienda = azienda;
      if (anno !== 'all') params.anno = anno;
      if (contrattoStatus !== 'all') params.contrattoStatus = contrattoStatus;
      if (tipoSoggetto !== 'all') params.tipoSoggetto = tipoSoggetto;
      if (contattiStatus !== 'all') params.contattiStatus = contattiStatus;
      if (contrattoStatus === 'all') params.includeStats = 'false';
      const res = await api.get('/passp-esposizione-contabile', { params });
      const netMs = Math.round(performance.now() - startedAt);
      setRows(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setPerf((prev) => ({
        ...prev,
        listNetMs: netMs,
        listApiMs: Number(res.data?.meta?.timingMs || 0),
        listBreakdown: res.data?.meta?.timings || null,
      }));
      if (res.data?.stats) {
        setStats({
          soggetti_univoci: Number(res.data?.stats?.soggetti_univoci || 0),
          con_contratti: Number(res.data?.stats?.con_contratti || 0),
          senza_contratti: Number(res.data?.stats?.senza_contratti || 0),
        });
      }
    } catch (_error) {
      toast.error('Errore nel caricamento Esposizione Contabile');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (contrattoStatus !== 'all') return;
    const startedAt = performance.now();
    try {
      const params = {
        page: 1,
        limit: 1,
        includeStats: 'true',
        statsOnly: 'true',
      };
      if (searchValue) params.search = searchValue;
      if (azienda !== 'all') params.azienda = azienda;
      if (anno !== 'all') params.anno = anno;
      if (tipoSoggetto !== 'all') params.tipoSoggetto = tipoSoggetto;
      if (contattiStatus !== 'all') params.contattiStatus = contattiStatus;
      const res = await api.get('/passp-esposizione-contabile', { params });
      const netMs = Math.round(performance.now() - startedAt);
      setStats({
        soggetti_univoci: Number(res.data?.stats?.soggetti_univoci || 0),
        con_contratti: Number(res.data?.stats?.con_contratti || 0),
        senza_contratti: Number(res.data?.stats?.senza_contratti || 0),
      });
      setPerf((prev) => ({
        ...prev,
        statsNetMs: netMs,
        statsApiMs: Number(res.data?.meta?.timingMs || 0),
        statsBreakdown: res.data?.meta?.timings || null,
      }));
    } catch (_error) {
      toast.error('Errore nel caricamento statistiche Esposizione Contabile');
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchData();
  }, [
    page,
    rowsPerPage,
    orderBy,
    order,
    searchValue,
    azienda,
    anno,
    contrattoStatus,
    tipoSoggetto,
    contattiStatus,
  ]);

  useEffect(() => {
    fetchStats();
  }, [searchValue, azienda, anno, tipoSoggetto, contattiStatus, contrattoStatus]);

  const handleRequestSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
    setPage(0);
  };

  const openAziendeDialog = async (clienteLabel, subjectKey) => {
    setSelectedClienteLabel(clienteLabel || '');
    setSelectedAziendeDetails([]);
    setAziendeDialogOpen(true);
    setAziendeDialogLoading(true);
    try {
      const res = await api.get(
        `/passp-esposizione-contabile/subject/${encodeURIComponent(
          String(subjectKey || '')
        )}/aziende`
      );
      setSelectedAziendeDetails(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (_error) {
      setSelectedAziendeDetails([]);
      toast.error('Errore nel caricamento aziende collegate');
    } finally {
      setAziendeDialogLoading(false);
    }
  };

  const openSubjectDetail = (row) => {
    const key = String(row?.soggetto_key || '').trim();
    if (!key) {
      toast.error('Dettaglio nominativo non disponibile');
      return;
    }
    navigate(`/passp-esposizione-contabile/${encodeURIComponent(key)}`);
  };

  const withContratti = useMemo(() => Number(stats.con_contratti || 0), [stats.con_contratti]);
  const withoutContratti = useMemo(
    () => Number(stats.senza_contratti || 0),
    [stats.senza_contratti]
  );

  const totalColumns = columns.length + 1;
  const empty = !loading && rows.length === 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            color: '#4f46e5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <WalletCards size={20} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Passpartout • Esposizione Contabile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verifica soggetti univoci Passpartout e presenza contratti in dossier
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Soggetti univoci"
            value={stats.soggetti_univoci}
            icon={Users}
            color="#0ea5e9"
            active={contrattoStatus === 'all'}
            onClick={() => {
              setContrattoStatus('all');
              setPage(0);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Con contratti"
            value={withContratti}
            icon={CheckCircle2}
            color="#22c55e"
            active={contrattoStatus === 'with'}
            onClick={() => {
              setContrattoStatus('with');
              setPage(0);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Senza contratti"
            value={withoutContratti}
            icon={XCircle}
            color="#ef4444"
            active={contrattoStatus === 'without'}
            onClick={() => {
              setContrattoStatus('without');
              setPage(0);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Totale filtrati"
            value={stats.soggetti_univoci}
            icon={WalletCards}
            color="#6366f1"
            active={contrattoStatus === 'all'}
            onClick={() => {
              setContrattoStatus('all');
              setPage(0);
            }}
          />
        </Grid>
      </Grid>

      <StyledCard>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'nowrap',
            overflowX: 'auto',
          }}
        >
          {perfDebugEnabled ? (
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
              Tempo lista: API {perf.listApiMs || 0} ms • rete {perf.listNetMs || 0} ms
              {contrattoStatus === 'all'
                ? ` • Stat: API ${perf.statsApiMs || 0} ms • rete ${perf.statsNetMs || 0} ms`
                : ''}
            </Typography>
          ) : null}
          {perfDebugEnabled && perf.listBreakdown ? (
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
              Breakdown lista: rows {perf.listBreakdown.rowsQueryMs || 0} ms • count{' '}
              {perf.listBreakdown.countQueryMs || 0} ms • statsRows {perf.listBreakdown.statsRowsQueryMs || 0} ms
              • customer remoto {perf.listBreakdown.remoteCustomerQueryMs || 0} ms • dossier remoto{' '}
              {perf.listBreakdown.remoteDossierQueryMs || 0} ms
            </Typography>
          ) : null}
          {perfDebugEnabled && contrattoStatus === 'all' && perf.statsBreakdown ? (
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
              Breakdown stat: rows {perf.statsBreakdown.rowsQueryMs || 0} ms • count{' '}
              {perf.statsBreakdown.countQueryMs || 0} ms • statsRows {perf.statsBreakdown.statsRowsQueryMs || 0} ms
              • customer remoto {perf.statsBreakdown.remoteCustomerQueryMs || 0} ms • dossier remoto{' '}
              {perf.statsBreakdown.remoteDossierQueryMs || 0} ms
            </Typography>
          ) : null}
          <TextField
            size="small"
            placeholder="Cerca per cliente, codice, CF o P.IVA..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Azienda</InputLabel>
            <Select
              value={azienda}
              label="Azienda"
              onChange={(e) => {
                setAzienda(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Tutte</MenuItem>
              {aziendeOptions.map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Anno</InputLabel>
            <Select
              value={anno}
              label="Anno"
              onChange={(e) => {
                setAnno(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Tutti</MenuItem>
              {anniOptions.map((item) => (
                <MenuItem key={String(item)} value={String(item)}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tipo soggetto</InputLabel>
            <Select
              value={tipoSoggetto}
              label="Tipo soggetto"
              onChange={(e) => {
                setTipoSoggetto(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="azienda">Azienda</MenuItem>
              <MenuItem value="persona_fisica">Persona fisica</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Contatti</InputLabel>
            <Select
              value={contattiStatus}
              label="Contatti"
              onChange={(e) => {
                setContattiStatus(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="no_email">Senza email</MenuItem>
              <MenuItem value="no_phone">Senza telefono</MenuItem>
              <MenuItem value="no_email_phone">Senza email e telefono</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                {columns.map((col) => (
                  <TableCell key={col.key}>
                    <TableSortLabel
                      active={orderBy === col.key}
                      direction={orderBy === col.key ? order : 'asc'}
                      onClick={() => handleRequestSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="center">Esito</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    <LoaderProgress />
                  </TableCell>
                </TableRow>
              )}

              {empty && (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center">
                    <Box sx={{ py: 5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Nessun cliente trovato
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row) => {
                  const clienteLabel =
                    row.ragione_sociale ||
                    row.denominazione ||
                    [row.cognome, row.nome].filter(Boolean).join(' ').trim() ||
                    '-';
                  const hasContratti = Boolean(row.has_contratti);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => openSubjectDetail(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack spacing={0.8}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            <Box
                              component="span"
                              sx={{
                                color: 'primary.main',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {clienteLabel}
                            </Box>
                          </Typography>
                          <Stack direction="row" spacing={0.9} alignItems="center">
                            <Mail size={16} color="#64748b" />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {row.email || '-'}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.9} alignItems="center">
                            <Phone size={16} color="#64748b" />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {row.telefono || '-'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>{row.codice || '-'}</TableCell>
                      <TableCell>{row.partita_iva || row.codice_fiscale || '-'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <Typography variant="body2">{row.azienda || '-'}</Typography>
                          {Number(row.aziende_count || 0) > 1 ? (
                            <Tooltip title="Vedi aziende collegate">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAziendeDialog(clienteLabel, row.soggetto_key);
                                }}
                                sx={{
                                  color: 'primary.main',
                                  bgcolor: 'primary.50',
                                  '&:hover': { bgcolor: 'primary.100' },
                                }}
                              >
                                <ChevronDown size={16} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {Number(row.aziende_count || 0) > 1 ? (
                            <Chip
                              size="small"
                              label={`${row.aziende_count} aziende`}
                              sx={{ fontWeight: 700, bgcolor: 'primary.50', color: 'primary.main' }}
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{row.anno || '-'}</TableCell>
                      <TableCell>{row.occorrenze || 1}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={Number(row.total_contratti || 0)}
                          sx={{
                            bgcolor: hasContratti ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)',
                            color: hasContratti ? 'success.dark' : 'error.dark',
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={row.motivo_no_match || '-'}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {hasContratti ? (
                              <CheckCircle2 size={24} color="#22c55e" />
                            ) : (
                              <XCircle size={24} color="#ef4444" />
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog
        open={aziendeDialogOpen}
        onClose={() => setAziendeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Aziende collegate • {selectedClienteLabel || '-'}
        </DialogTitle>
        <DialogContent>
          {aziendeDialogLoading ? <LoaderProgress /> : null}
          <Stack spacing={1.2} sx={{ mt: 0.5 }}>
            {selectedAziendeDetails.map((item, idx) => (
              <Card key={`${item.azienda}-${idx}`} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={1.3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {item.azienda_nome || item.azienda}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Codice azienda: {item.azienda || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Codice cliente: {item.cliente_codice || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      CF/P.IVA azienda: {item.cod_fis_piva || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tipo: {item.tipo || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Stato: {item.stato || '-'} • Anno: {item.anno || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            ))}
            {!aziendeDialogLoading && !selectedAziendeDetails.length ? (
              <Typography variant="body2" color="text.secondary">
                Nessun dettaglio azienda disponibile
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
